import { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { IoIosArrowDown } from "react-icons/io";
import sectionSixHero from "../../assets/images/section-six-hero.png";
import { API_CONFIG } from "../../config/apiConfig";
const IoIosArrowDownIcon = IoIosArrowDown as React.ElementType;

interface ConnectWithUsFormProps {
  wrapperClassName?: string;
  imageClassName?: string;
}

const coreTypes = [
  "USA",
  "Canada",
  "United Kingdom",
  "Australia",
  "UAE",
  "Saudi Arabia",
];

const coreServices = [
  "Recruitment & Overseas Employment",
  "Visa & Emigration Services",
  "Attestation & Document Services",
  "Education & Study Abroad",
  "Travel & Holidays",
  "Specialised Services (Exams & related services)",
  "Registrations & Appointments",
];

const ConnectWithUsForm: React.FC<ConnectWithUsFormProps> = ({
  wrapperClassName = "bg-primary-lightblue md:pt-5 lg:pb-0 pb-10 px-4",
  imageClassName = "w-full h-auto max-w-[700px] mx-auto rounded",
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country_code: "+91",
    visaCountry: "",
    visaService: "",
    message: "",
    reason: "General Enquiry",
  });
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "phone") setPhoneError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.phone.length < 10) {
      setPhoneError("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        country_code: formData.country_code,
        reason: formData.reason,
        core_type: formData.visaCountry || "",
        core_services: formData.visaService || "",
        source_url: window.location.href,
        message: formData.message,
      };

      const response = await axios.post(`${API_CONFIG.BASE_URL}/contact`, payload, {
        headers: API_CONFIG.HEADERS,
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: response.data.message || "Thank you for contacting us! We will get back to you shortly.",
          confirmButtonColor: "#002661",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          country_code: "+91",
          visaCountry: "",
          visaService: "",
          message: "",
          reason: "General Enquiry",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: response.data.message || "Something went wrong. Please try again.",
          confirmButtonColor: "#E74C3C",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error?.response?.data?.message || "Server error. Please check your connection.",
        confirmButtonColor: "#E74C3C",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={wrapperClassName}>
      <div className="max-w-7xl mx-auto px-4 lg:px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={sectionSixHero} alt="Woman holding passport" className={imageClassName} />
          </div>

          <div className="pb-4">
            <h2 className="xl:text-5xl md:text-4xl text-3xl font-extrabold text-primary-navyblue mb-4">
              Connect with us!
            </h2>
            <p className="text-primary-gray font-medium mb-8">
              Bridging talent with global opportunities through recruitment, visa, and document services.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  placeholder="Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-[#25224A5C] rounded-[2px] bg-white focus:outline-none"
                />
                <input
                  placeholder="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full px-3 py-2 border border-[#25224A5C] rounded-[2px] bg-white focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <PhoneInput
                    country={"in"}
                    value={formData.country_code + formData.phone}
                    onChange={(value, phoneData: any) => {
                      const dialCode = phoneData?.dialCode || "91";
                      const phoneNumber = value.startsWith(dialCode)
                        ? value.slice(dialCode.length)
                        : value;
                      setFormData((prev) => ({
                        ...prev,
                        phone: phoneNumber,
                        country_code: `+${dialCode}`,
                      }));
                      setPhoneError("");
                    }}
                    containerClass="!w-full"
                    inputClass="!w-full !h-[42px] !border-[#25224A5C] !rounded-[2px] !font-medium focus:outline-none cursor-pointer"
                    buttonClass="!border-[#25224A5C] !bg-white !rounded-l-[2px]"
                    dropdownClass="!bg-white"
                    enableSearch={true}
                  />
                  {phoneError && <p className="text-primary-red text-[10px] absolute -bottom-5">{phoneError}</p>}
                </div>

                <label className="block relative">
                  <select
                    value={formData.visaCountry}
                    onChange={(e) => handleInputChange("visaCountry", e.target.value)}
                    className="appearance-none block w-full px-3 py-2 pr-10 border border-[#25224A5C] rounded-[2px] bg-white text-gray-600 font-medium focus:outline-none cursor-pointer"
                    required
                  >
                    <option value="" disabled>
                      Select your Core type
                    </option>
                    {coreTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                    <IoIosArrowDownIcon size={18} />
                </div>
              </label>
              </div>

              <label className="block relative">
                <select
                  value={formData.visaService}
                  onChange={(e) => handleInputChange("visaService", e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-[#25224A5C] rounded-[2px] bg-white text-gray-600 font-medium focus:outline-none cursor-pointer"
                  required
                >
                  <option value="" disabled>
                    Select Our Core Services
                  </option>
                  {coreServices.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                  <IoIosArrowDownIcon size={18} />
                </div>
              </label>

              <textarea
                placeholder="Message"
                value={formData.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
                className="w-full px-3 py-2 border border-[#25224A5C] rounded-[2px] bg-white h-24 resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
              />

              <button
                type="submit"
                disabled={loading}
                className={`${loading ? "bg-gray-400 cursor-not-allowed" : "bg-primary-red hover:bg-red-700"} text-primary-white font-bold py-3 px-8 rounded-none`}
              >
                {loading ? "SENDING..." : "APPLY NOW"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectWithUsForm;
